import React, { useState, useEffect, useRef } from 'react';
import { Search, Phone, MessageSquare, Mail, Linkedin, RotateCcw, Power, Activity, TrendingUp, Zap } from 'lucide-react';

interface AgentControlPanelProps {
  onOpenDemoModal: () => void;
}

interface Agent {
  id: string;
  name: string;
  icon: React.ReactNode;
  color: string;
  avatar: string;
  metrics: {
    label: string;
    value: number;
    suffix: string;
  };
}

const AgentControlPanel: React.FC<AgentControlPanelProps> = ({ onOpenDemoModal }) => {
  const [activeAgents, setActiveAgents] = useState<boolean[]>(Array(6).fill(false));
  const [agentMetrics, setAgentMetrics] = useState<{ [key: string]: number }>({});
  const [animationStep, setAnimationStep] = useState(0);

  const agents: Agent[] = [
    {
      id: 'leadgen',
      name: 'Lead Gen Agent',
      icon: <Search className="w-5 h-5" />,
      color: 'brand-blue',
      avatar: '/leadgenagent.png',
      metrics: { label: 'Leads found', value: 847, suffix: '/day' }
    },
    {
      id: 'call',
      name: 'Call Agent',
      icon: <Phone className="w-5 h-5" />,
      color: 'brand-green-teal',
      avatar: '/callagent.png',
      metrics: { label: 'Calls made', value: 156, suffix: '/day' }
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp Agent',
      icon: <MessageSquare className="w-5 h-5" />,
      color: 'salespad-cyan',
      avatar: '/whatsappagent.png',
      metrics: { label: 'Messages sent', value: 1243, suffix: '/day' }
    },
    {
      id: 'email',
      name: 'Email Agent',
      icon: <Mail className="w-5 h-5" />,
      color: 'brand-blue',
      avatar: '/emailagent.png',
      metrics: { label: 'Emails sent', value: 2156, suffix: '/day' }
    },
    {
      id: 'linkedin',
      name: 'LinkedIn Agent',
      icon: <Linkedin className="w-5 h-5" />,
      color: 'brand-green-teal',
      avatar: '/linkedinagent.png',
      metrics: { label: 'Connections', value: 89, suffix: '/day' }
    },
    {
      id: 'followup',
      name: 'Follow-up Agent',
      icon: <RotateCcw className="w-5 h-5" />,
      color: 'salespad-cyan',
      avatar: '/followupagent.png',
      metrics: { label: 'Follow-ups', value: 324, suffix: '/day' }
    }
  ];

  // Initialize metrics
  useEffect(() => {
    const initialMetrics: { [key: string]: number } = {};
    agents.forEach(agent => {
      initialMetrics[agent.id] = 0;
    });
    setAgentMetrics(initialMetrics);
  }, []);

  // Continuous animation cycle
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (animationStep < agents.length) {
      // Activate current agent
      setActiveAgents(prev => {
        const newState = [...prev];
        newState[animationStep] = true;
        return newState;
      });
      
      // Move to next agent after 1 second
      timer = setTimeout(() => {
        setAnimationStep(prev => prev + 1);
      }, 1000);
    } else if (animationStep === agents.length) {
      // All agents are on, wait 2 seconds then turn all off
      timer = setTimeout(() => {
        setActiveAgents(prev => {
          const newState = Array(agents.length).fill(false);
          return newState;
        });
        setAnimationStep(prev => prev + 1);
      }, 2000);
    } else {
      // All agents are off, wait 1 second then restart
      timer = setTimeout(() => {
        setAnimationStep(0);
      }, 1000);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [animationStep, agents.length]);

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  return (
    <div className="relative w-full max-w-7xl mx-auto">
      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center mb-8 sm:mb-10 lg:mb-12">
        {/* Left Column - Headline and Text */}
        <div className="text-center lg:text-left lg:ml-16">
          <h2 className="text-text-primary font-black text-3xl sm:text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl leading-tight tracking-tight mb-3 sm:mb-4">
            <div>Switch On Your</div>
            <div>AI Sales Team</div>
          </h2>
          <div className="w-16 sm:w-20 lg:w-24 h-1 bg-gradient-brand mb-4 sm:mb-6 mx-auto lg:mx-0"></div>
          <div className="hidden lg:block mb-4 sm:mb-6">
            <button
              onClick={onOpenDemoModal}
              className="inline-block px-6 py-3 rounded-xl bg-gradient-cta text-cta-text font-bold text-base transition-all duration-300 hover:shadow-xl hover:scale-105 cta-hover-overlay"
            >
              Launch Your AI Sales Team
            </button>
          </div>
        </div>

        {/* Right Column - Agent Grid (2x3) */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 max-w-md mx-auto lg:max-w-none">
          {agents.map((agent, index) => {
            const isActive = activeAgents[index];
            
            return (
              <div
                key={agent.id}
                className="relative bg-bg-surface border rounded-lg sm:rounded-xl p-2 sm:p-3 lg:p-4 transition-all duration-500 min-h-[60px] sm:h-18 lg:h-20 border-card-border"
                style={{
                  borderColor: isActive ? 'rgba(47, 123, 255, 0.5)' : '',
                  boxShadow: isActive ? '0 0 40px 0 rgba(61,139,255,0.35)' : '',
                  transform: isActive ? 'scale(1.02)' : 'scale(1)',
                }}
              >
                {/* Single Row: Avatar, Name, and Power Switch */}
                <div className="flex items-center justify-between h-full">
                  {/* Avatar */}
                  <div className={`
                    w-10 h-10 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full overflow-hidden border-2 transition-all duration-500 flex-shrink-0
                    ${isActive 
                      ? 'border-brand-blue shadow-glow-blue' 
                      : 'border-card-border grayscale opacity-50'
                    }
                  `}>
                    <img
                      src={`/landing-page${agent.avatar}`}
                      alt={agent.name}
                      className="w-full h-full object-cover object-center"
                    />
                  </div>

                  {/* Agent Name */}
                  <div className="flex-1 mx-2 sm:mx-3 min-w-0">
                    <h4 className={`
                        font-semibold text-xs sm:text-sm lg:text-base transition-all duration-500 truncate
                      ${isActive ? 'text-text-primary' : 'text-text-secondary/60'}
                    `}>
                      {agent.name}
                    </h4>
                  </div>

                  {/* Power Switch */}
                  <div 
                    className={`
                      w-10 h-10 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full border-2 flex items-center justify-center transition-all duration-300 flex-shrink-0
                      ${isActive 
                        ? 'border-brand-green-teal bg-brand-green-teal shadow-glow-blue' 
                        : 'border-card-border bg-transparent'
                      }
                    `}
                  >
                    {isActive ? (
                      <Power className="w-3 h-3 sm:w-2.5 sm:h-2.5 lg:w-4 lg:h-4 text-white animate-pulse" />
                    ) : (
                      <Power className="w-4 h-4 sm:w-3.5 sm:h-3.5 lg:w-5 lg:h-5 text-text-secondary/50" />
                    )}
                  </div>
                </div>

                {/* Activation Effect */}
                {isActive && (
                  <div 
                    className="absolute inset-0 rounded-xl border-2 border-brand-blue/30 animate-ping pointer-events-none"
                    style={{ animationDuration: '2s', animationIterationCount: '1' }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Mobile Button Below Agent Boxes */}
      <div className="block sm:hidden mt-6 text-center">
        <button
          onClick={onOpenDemoModal}
          className="inline-block px-6 py-3 rounded-xl bg-gradient-cta text-cta-text font-bold text-base transition-all duration-300 hover:shadow-xl hover:scale-105 cta-hover-overlay"
        >
          Launch Your AI Sales Team
        </button>
      </div>
    </div>
  );
};

export default AgentControlPanel;